import type {AttributeDefinition} from '@/types/attribute-definition'
import type {SetAttributeFormValue} from '@/features/onboarding/types'

const PLACEHOLDER_RE = /\{(\w+)\}/g

/**
 * Render a TypeValue.settings.displayNameTemplate (e.g. `"{firstName} {lastName}"`)
 * against a flat name -> string map.
 *
 * Rules:
 * - Empty / null / undefined template -> empty string
 * - Missing placeholder values substitute as the empty string
 * - Single-pass substitution (no recursion)
 * - Whitespace runs (including those produced by empty interior placeholders)
 *   are collapsed to a single space; the whole result is trimmed.
 *
 * Mirrors the substitution logic in {@file frontend/src/services/mock/mock-store.ts}
 * (search `displayNameTemplate.replace`). The real backend uses the same `{name}`
 * syntax. The whitespace-collapse pass on top fixes the "double space from a
 * missing middle placeholder" gap raised by HQG #413.
 */
export function renderDisplayNameTemplate(
    template: string | null | undefined,
    attributesByName: Record<string, string>,
): string {
    if (!template) return ''
    return template
        .replace(PLACEHOLDER_RE, (_, key: string) => attributesByName[key] ?? '')
        .replace(/\s+/g, ' ')
        .trim()
}

/**
 * Build a flat `name -> stringValue` map from attribute definitions plus the
 * onboarding form's `SetAttributeFormValue[]`. Attribute definitions provide
 * the `id -> name` mapping; values may be string, number, or first-element of
 * a multi-value list.
 *
 * Used to feed {@link renderDisplayNameTemplate} from form state.
 */
export function buildAttributeNameMap(
    definitions: AttributeDefinition[],
    values: SetAttributeFormValue[],
): Record<string, string> {
    const knownNames = new Set(definitions.map(d => d.name))

    const result: Record<string, string> = {}
    for (const val of values) {
        if (!knownNames.has(val.attributeName)) continue
        result[val.attributeName] = coerceToString(val)
    }
    return result
}

function coerceToString(val: SetAttributeFormValue): string {
    if (val.valueString !== undefined && val.valueString !== null) return val.valueString
    if (val.valueNumber !== undefined && val.valueNumber !== null) return String(val.valueNumber)
    if (val.valueBoolean !== undefined && val.valueBoolean !== null) return String(val.valueBoolean)
    if (val.valueDate !== undefined && val.valueDate !== null) return val.valueDate
    if (val.valueTimestamp !== undefined && val.valueTimestamp !== null) return val.valueTimestamp
    if (Array.isArray(val.values) && val.values.length > 0) return String(val.values[0] ?? '')
    return ''
}
